import React, { useState, useCallback, useEffect, useRef, memo } from 'react';
import { useReactFlow } from '@xyflow/react';
import { Map as MapIcon, Grid3x3, Maximize2, ZoomIn, ZoomOut } from 'lucide-react';
import { CanvasMiniMap } from './MiniMap/CanvasMiniMap';
import { useCanvasStore } from '../../stores/canvasStore';

const MINIMAP_WIDTH = 216;
const MINIMAP_HEIGHT = 110;

// 独立的 zoom 显示组件 — zoomPercent 变化只重渲染这个 <span>，不触发 MiniMapToolbar 重渲染
// 避免 zoomIn/zoomOut 按钮点击后 store subscribe 触发 zoomPercent 变化 → 整个 toolbar reconciliation → 与光流 rAF 竞争
// 支持点击进入编辑模式，手动输入 10%-300% 的显示比例
const MIN_ZOOM_PERCENT = 10;
const MAX_ZOOM_PERCENT = 300;

const ZoomPercentDisplay = memo(() => {
  const [zoomPercent, setZoomPercent] = useState(() =>
    Math.round(useCanvasStore.getState().zoom * 100)
  );
  const [isEditing, setIsEditing] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);
  const { zoomTo } = useReactFlow();

  useEffect(() => {
    let prevZoom = useCanvasStore.getState().zoom;
    return useCanvasStore.subscribe((state) => {
      if (state.zoom !== prevZoom) {
        prevZoom = state.zoom;
        const percent = Math.round(state.zoom * 100);
        setZoomPercent(prev => prev !== percent ? percent : prev);
      }
    });
  }, []);

  const startEditing = useCallback(() => {
    setInputValue(String(zoomPercent));
    setIsEditing(true);
  }, [zoomPercent]);

  // 延迟聚焦，等 input 渲染出来
  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [isEditing]);

  const commitEdit = useCallback(() => {
    setIsEditing(false);
    const parsed = parseInt(inputValue, 10);
    if (isNaN(parsed)) return;
    // clamp 到 10%-300%
    const clamped = Math.max(MIN_ZOOM_PERCENT, Math.min(MAX_ZOOM_PERCENT, parsed));
    const newZoom = clamped / 100;
    zoomTo(newZoom);
    setZoomPercent(clamped);
  }, [inputValue, zoomTo]);

  const handleKeyDown = useCallback((e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      commitEdit();
    } else if (e.key === 'Escape') {
      e.preventDefault();
      setIsEditing(false);
    }
  }, [commitEdit]);

  if (isEditing) {
    return (
      <input
        ref={inputRef}
        type="number"
        min={MIN_ZOOM_PERCENT}
        max={MAX_ZOOM_PERCENT}
        value={inputValue}
        onChange={(e) => setInputValue(e.target.value)}
        onBlur={commitEdit}
        onKeyDown={handleKeyDown}
        className="w-[36px] bg-white/10 text-white text-[10px] font-medium tabular-nums text-center rounded px-0.5 outline-none border border-white/20 focus:border-white/40 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
      />
    );
  }

  return (
    <span
      onClick={startEditing}
      className="text-[10px] font-medium text-white/60 tabular-nums cursor-text hover:text-white/90 transition-colors select-none"
      title="点击输入显示比例 (10%-300%)"
    >
      {zoomPercent}%
    </span>
  );
});
ZoomPercentDisplay.displayName = 'ZoomPercentDisplay';

export const MiniMapToolbar: React.FC = memo(() => {
  const [showMiniMap, setShowMiniMap] = useState(true);
  const [snapToGrid, setSnapToGrid] = useState(false);
  const { zoomIn, zoomOut, fitView } = useReactFlow();
  const containerRef = useRef<HTMLDivElement>(null);

  const handleZoomIn = useCallback(() => zoomIn(), [zoomIn]);
  const handleZoomOut = useCallback(() => zoomOut(), [zoomOut]);
  const handleFitView = useCallback(() => fitView({ padding: 0.2 }), [fitView]);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const handleWheel = (e: WheelEvent) => {
      if (e.ctrlKey) e.preventDefault();
    };
    el.addEventListener('wheel', handleWheel, { passive: false });
    return () => el.removeEventListener('wheel', handleWheel);
  }, []);

  return (
    <div ref={containerRef} className="flex flex-col gap-2 pointer-events-auto">
      {/* 小地图 — Canvas 渲染，GPU 合成层，rAF 驱动
          折叠时用 opacity:0 + pointer-events:none（不用 display:none），
          避免 display:none 时 Canvas 尺寸归零导致恢复时需要重新初始化 + rAF 停顿
          visible prop 让 CanvasMiniMap 在折叠时暂停 rAF，节省 CPU
          CanvasMiniMap 有 memo，showMiniMap 变化只触发 visible prop 更新，不重渲染整个组件树 */}
      <div
        className="rounded-xl overflow-hidden border border-white/10 shadow-lg relative transition-opacity"
        style={{
          width: MINIMAP_WIDTH,
          height: MINIMAP_HEIGHT,
          opacity: showMiniMap ? 1 : 0,
          pointerEvents: showMiniMap ? 'auto' : 'none',
        }}
      >
        <CanvasMiniMap
          width={MINIMAP_WIDTH}
          height={MINIMAP_HEIGHT}
          maskColor="rgba(0, 0, 0, 0.8)"
          bgColor="#27272a"
          pannable
          zoomable
          visible={showMiniMap}
        />
      </div>

      {/* 地图工具栏 */}
      <div className="flex items-center gap-1 px-2 py-1.5 bg-[#1c1c1e]/95 backdrop-blur-xl border border-white/10 rounded-xl shadow-lg" style={{ width: MINIMAP_WIDTH }}>
        {/* 开关小地图 */}
        <button
          onClick={() => setShowMiniMap(v => !v)}
          className={`flex items-center justify-center w-7 h-7 rounded-md transition-all flex-shrink-0 ${
            showMiniMap
              ? 'bg-white/15 text-white'
              : 'text-white/50 hover:text-white hover:bg-white/10'
          }`}
          title={showMiniMap ? '关闭小地图' : '开启小地图'}
        >
          <MapIcon size={14} />
        </button>

        {/* 网格吸附 */}
        <button
          onClick={() => setSnapToGrid(v => !v)}
          className={`flex items-center justify-center w-7 h-7 rounded-md transition-all flex-shrink-0 ${
            snapToGrid
              ? 'bg-white/15 text-white'
              : 'text-white/50 hover:text-white hover:bg-white/10'
          }`}
          title={snapToGrid ? '关闭网格吸附' : '开启网格吸附'}
        >
          <Grid3x3 size={14} />
        </button>

        {/* 重置视窗 */}
        <button
          onClick={handleFitView}
          className="flex items-center justify-center w-7 h-7 rounded-md text-white/50 hover:text-white hover:bg-white/10 transition-all flex-shrink-0"
          title="重置视窗"
        >
          <Maximize2 size={14} />
        </button>

        <div className="w-px h-4 bg-white/10 mx-1 flex-shrink-0" />

        {/* 缩小 */}
        <button
          onClick={handleZoomOut}
          className="flex items-center justify-center w-7 h-7 rounded-md text-white/50 hover:text-white hover:bg-white/10 transition-all flex-shrink-0"
          title="缩小"
        >
          <ZoomOut size={14} />
        </button>

        {/* 画布比例显示 — 独立 memo 组件，zoom 变化只重渲染这个 span */}
        <div className="flex items-center justify-center min-w-[36px] px-1">
          <ZoomPercentDisplay />
        </div>

        {/* 放大 */}
        <button
          onClick={handleZoomIn}
          className="flex items-center justify-center w-7 h-7 rounded-md text-white/50 hover:text-white hover:bg-white/10 transition-all flex-shrink-0"
          title="放大"
        >
          <ZoomIn size={14} />
        </button>
      </div>
    </div>
  );
});

MiniMapToolbar.displayName = 'MiniMapToolbar';
